import { useQueryClient } from '@tanstack/vue-query';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { mixPanelEvents } from '@/common/constants';
import { useKeywordListNamesQueryOptions, useKeywordListTermsQueryOptions } from '@/common/queries/keyword-list';
import { useIntercomStore } from '@/common/stores/intercom';
import { useTrackedToast } from '@/composables/useTrackedToast';
import { track } from '@/helpers/analytics/mixpanel';
import { axios, sanctumCookie } from '@/helpers/axios';
import { useKeywordListNamesQueryOptions as useDashboardKeywordListNamesQueryOptions } from '@/modules/dashboard/queries/keywordlist';
import { useNotificationStore } from '@/modules/layout/stores/notification';

export interface ListItem {
  id: number;
  memberId: number;
  name: string;
  comments: string;
  flags: string;
  title_builder: string;
  marketplace: string;
  is_deleted: number;
  listing_id: string;
  listing_url: string;
}

export interface keywordListItem {
  id: number;
  listId: number;
  keyword: string;
}

export interface LatestTermsAddedRemoved {
  term: string;
  value: boolean;
}

export const useKeywordListStore = defineStore('keywordLists', () => {
  const queryClient = useQueryClient();
  const toast = useTrackedToast();
  const intercomStore = useIntercomStore();

  const loading = ref(false);
  const loadingKeywordsList = ref(false);
  const addingTerms = ref(false);
  const removingList = ref(false);
  const removingKeywordFromList = ref(false);
  const lists = ref<ListItem[]>([]);
  const listsLoading = ref<Set<number>>(new Set());
  const keywordsList = ref<keywordListItem[]>([]);
  const lastActiveListId = ref<number | null>(null);
  const { notification } = storeToRefs(useNotificationStore());

  // For dashboard widgets watching terms addition/removal
  const watchTermsUpdate = ref({} as LatestTermsAddedRemoved);
  // Ensure we only refresh Intercom once per app load for list additions
  const hasRefreshedIntercomForListAdd = ref(false);

  function invalidateQueries() {
    void queryClient.invalidateQueries(useDashboardKeywordListNamesQueryOptions());
    void queryClient.invalidateQueries(useKeywordListNamesQueryOptions());
    void queryClient.invalidateQueries(useKeywordListTermsQueryOptions());
  }

  const getLists = async () => {
    loading.value = true;

    try {
      const response = await axios.get('/keywordlist/names');
      lists.value = response.data;
    } catch(e) { /* empty */ }
    loading.value = false;
  };

  const getKeywordsList = async ():Promise<void> => {
    loadingKeywordsList.value = true;

    try {
      const response = await axios.get('/keywordlist/terms');
      keywordsList.value = response.data;
    } catch(e) { /* empty */ }
    loadingKeywordsList.value = false;
  };

  const keywordsInListSet = computed(() => {
    const keywordSet = new Set<string>();
    if (lastActiveListId.value !== null) {
      keywordsList.value.forEach((item) => {
        if (item.listId === lastActiveListId.value) {
          keywordSet.add(item.keyword);
        }
      });
    }
    return keywordSet;
  });

  const keywordsInAnyListSet = computed(() => {
    const allKeywordSet = new Set<string>();
    keywordsList.value.forEach((item) => {
      if (item?.keyword) {
        allKeywordSet.add(item.keyword.toLowerCase());
      }
    });
    
    return allKeywordSet;
  });

  const removeList = async (listId: number) => {
    removingList.value = true;
    try {
      await axios.post('/keywordlist/delete-kl', {
        id: listId,
      });

      // Optimistic update for the list and keyword list without calling getLists again
      lists.value = lists.value.filter((item) => item.id !== listId);
      keywordsList.value = keywordsList.value.filter((item) => item.listId !== listId);

      notification.value = {
        type: 'success',
        message: 'List has been deleted successfully.',
        title: 'Success',
        life: 3000,
      };
    } catch(e) {
      notification.value = {
        type: 'error',
        message: e.response.data.error || 'Failed to delete the list. Please try again later.',
        title: 'Error',
        life: 3000,
      };
    } finally {
      invalidateQueries();
      removingList.value = false;
    }
  };

  const removeKeywordFromLists = async (keyword: string):Promise<void> => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    let keywordsListIds = keywordsList.value
      .filter((item) => item.keyword.trim().toLowerCase() === normalizedKeyword)
      .map((item) => item.id);

    if (!keywordsListIds.length) {
      await getKeywordsList();
      keywordsListIds = keywordsList.value
        .filter((item) => item.keyword.trim().toLowerCase() === normalizedKeyword)
        .map((item) => item.id);
    }

    if (!keywordsListIds.length) {
      return;
    }

    await axios.post('/keywordlist/delete-kl-term', {
      ids: keywordsListIds,
    });

    // Optimistic update for the keyword list without calling getKeywordsList again
    keywordsList.value = keywordsList.value.filter((keywordList) => keywordsListIds.indexOf(keywordList.id) === -1);

    invalidateQueries();
  };

  const removeKeywordList = async (keywordListId: number):Promise<void> => {
    removingKeywordFromList.value = true;
    await axios.post('/keywordlist/delete-kl-term', {
      ids: [keywordListId],
    });
    removingKeywordFromList.value = false;
    // TODO: Toast Errors
    // Optimistic update for the keyword list without calling getKeywordsList again
    keywordsList.value = keywordsList.value.filter((item) => item.id !== keywordListId);

    invalidateQueries();
  };

  const addTermsToList = async (listId: number, keywords: string):Promise<void> => {
    addingTerms.value = true;

    lastActiveListId.value = listId;
    listsLoading.value.add(listId);
    try {
      const response = await axios.post<keywordListItem[] | keywordListItem>('/keywordlist/save-keyword', {
        listId,
        keyword: keywords,
      });


      // Optimistic update for the keyword list without calling getKeywordsList again
      if (Array.isArray(response.data)) {
        response.data.forEach((item: keywordListItem) => {
          keywordsList.value.push(item);
          lastActiveListId.value = listId; // update the last active list id
        });
      } else {
        const item = response.data as keywordListItem;
        keywordsList.value.push(item);
        lastActiveListId.value = listId; // update the last active list id
      }

      addingTerms.value = false;

      track(mixPanelEvents.ADDED_KEYWORD_TO_LIST, {
        Keyword: keywords,
      });

      // Refresh Intercom user data after the first keyword add only
      if (!hasRefreshedIntercomForListAdd.value) {
        hasRefreshedIntercomForListAdd.value = true;
        void intercomStore.fetchIntercom();
      }

      invalidateQueries();
    } catch (e) {
      if (e.response?.data.quota_exceeded) {
        toast.add({
          severity: 'warn',
          summary: 'Unable to save the keyword',
          detail: e.response?.data.error || 'Unknown error occurred',
          life: 3000,
        });
      } else {
        throw e;
      }
    } finally {
      listsLoading.value.delete(listId);
    }
  };

  const addKeywordToDefaultList = async (keyword: string):Promise<void> => addTermsToList(lastActiveListId.value, keyword);

  const setLoadingKeywordsList = (value: boolean) => {
    loadingKeywordsList.value = value;
  };

  const removeKeywordFromSearchHistory = async (keyword: string):Promise<boolean> => {

    try {
      await sanctumCookie();
      const response = await axios.post('/keyword-tool/search-history/remove', { keyword });
      const { data } = response;

      const success = data.status === 200;

      notification.value = {
        type: success ? 'success' : 'error',
        message: data.message,
        title: success ? 'Success' : 'Error',
        life: 3000,
      };

      return success;

    } catch(e) {
      notification.value = {
        type: 'error',
        message: e.response.data.error || 'Failed to delete the keyword. Please try again later.',
        title: 'Error',
        life: 3000,
      };
      return false;
    }

  };

  const removeAllKeywordSearchHistory = async ():Promise<boolean> => {

    try {
      const response = await axios.get('/keyword-tool/search-history/remove/all');
      const { data } = response;

      const success = data.status === 200;

      notification.value = {
        type: success ? 'success' : 'error',
        message: data.message,
        title: success ? 'Success' : 'Error',
        life: 3000,
      };

      return success;

    } catch(e) {
      notification.value = {
        type: 'error',
        message: e.response.data.error || 'Failed to delete the keyword. Please try again later.',
        title: 'Error',
        life: 3000,
      };
      return false;
    }

  };

  const updateWatchTerms = (data: LatestTermsAddedRemoved) => {
    watchTermsUpdate.value = data;
  };

  return {
    loading,
    loadingKeywordsList,
    setLoadingKeywordsList,
    keywordsList,
    keywordsInListSet,
    keywordsInAnyListSet,
    getKeywordsList,
    addTermsToList,
    removeKeywordList,
    removeKeywordFromLists,
    addKeywordToDefaultList,
    lastActiveListId,
    addingTerms,
    removeList,
    removingKeywordFromList,
    removingList,
    getLists,
    listsLoading,
    lists,
    removeKeywordFromSearchHistory,
    removeAllKeywordSearchHistory,
    watchTermsUpdate,
    updateWatchTerms,
  };
});
